// Adaptors
import { MetaOSHostAdaptor } from "../MetaOSHostAdaptor";
// Constants
import { AppPageIdFlavor, ClientFlavor, Environment, TeamsAppId } from "@ms/uno-constants/lib/local/AppConstants";
import { TeamsChannelType, TeamsRingId } from "@ms/uno-constants/lib/local/TeamsConstants";
import { TeamsTheme } from "@ms/uno-constants/lib/local/ThemeConstants";
import { MarkerTag } from "@ms/uno-telemetry/lib/local/performance/constants/PerformanceMarkerConstants";
// Models
import { PlannerTheme } from "@ms/uno-constants/lib/local/ThemeConstants";
import { isV1UnoEntityId } from "@ms/uno-routing";
// Meta hub sdk
import * as metaOSAppSDK from "@microsoft/teams-js-uno";
// Telemetry
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { generateLogStringFromError } from "@ms/uno-telemetry/lib/local/utilities/LogUtilities";
// Utilities
import { ErrorUtilities } from "@ms/uno-errors/lib/local/utilities/ErrorUtilities";
import { generateDeepLinkFromRoute } from "@ms/uno-routing";
import { stringToEnum } from "@ms/uno-utilities/lib/local/EnumUtilities";
import { generateGuid } from "@ms/uno-utilities/lib/local/Guid";
import { computeTeamsRootUrl } from "@ms/uno-utilities/lib/local/TeamsUtilities";
import { isChannelTab } from "../../utils/LegacyTeamsDeepLinkUtils";
import { isTeamsAppEntityId, tryParseAppRoute, tryParseMessageRoute, tryParseTabRoute } from "../../utils/TeamsRouteUtils";
import { pageIdToAppIdMap } from "../MetaOSHostsConstants";
/**
 * TeamsAdaptor provides Teams-specific implementations for MetaOSHostAdaptor.
 *
 * This adaptor handles Teams context validation, theme mapping, environment-specific root URL computation,
 * and Teams app caching integration. It ensures that the app configuration matches the current Teams context,
 * manages Teams-specific routing, and registers handlers for Teams app lifecycle events such as load and unload.
 *
 * @remarks
 * - Validates ringId consistency between session metadata and Teams context to prevent feature leaks.
 * - Maps Teams themes to Uno themes for consistent UI experience.
 * - Computes the root URL for different Teams environments (e.g., commercial, GCC, DoD).
 * - Integrates with Teams app caching to optimize app startup and navigation.
 * - Parses and resolves routes from Teams deep links and cached load contexts.
 *
 * @extends MetaOSHostAdaptor
 */ export class TeamsAdaptor extends MetaOSHostAdaptor {
    /**
     * Ensures that app config is valid for the current Teams context.
     * Checks whether ringId in sessionMetaData matches ringId from teamsContext
     * @returns validation error message. null if valid.
     */ validateRingId() {
        // Ensure that the ringId in sessionMetaData matches what teamsContext gives us
        const { ringId: sessionRingId } = this.getConfig().sessionMetaData;
        if (typeof sessionRingId === "string" && sessionRingId.length > 0 && this.ringId && // Ensure both teamsContext ringId and session's ringId are valid/non-empty
        this.ringId !== sessionRingId) {
            // The teamsContext ringId does not match the session's ringId
            // This could mean that the current session contains feature flights not meant for this user (feature leak)
            return `Teams client ringId does not match session ringId [ClientRingId=${this.ringId}][SessionRingId=${sessionRingId}][SessionHomeRingId=${this.homeRingId ?? ""}]`;
        }
        return null;
    }
    generateDeepLink(route) {
        // In case of create Task from message, we don't have a page id info.
        // App id is calculated based on the page id, so we need to calculate both pageId and appId here.
        let pageId = this.pageId;
        if (!pageId) {
            const environment = this.getConfig().sessionMetaData.environment;
            switch(environment){
                case Environment.Ppe:
                    pageId = AppPageIdFlavor.PPE;
                    break;
                case Environment.Prod:
                case Environment.Gcc:
                case Environment.Gcch:
                case Environment.Dod:
                case Environment.Agc:
                case Environment.Bleu:
                case Environment.Delos:
                    // Same as Prod
                    pageId = AppPageIdFlavor.Prod;
                    break;
                default:
                    ErrorUtilities.unreachableCase(environment, (log)=>{
                        this.loggers?.traceLogger.logTrace(0x1e19e81b /* tag_4g461 */ , TraceLevel.Error, log);
                    });
                    pageId = AppPageIdFlavor.Prod;
                    break;
            }
        }
        const channelTab = this.isChannelTab ?? (isChannelTab(pageId) || isV1UnoEntityId(pageId));
        const appId = this.appId ?? this.getHostAppId(channelTab, pageId);
        if (!pageId || !appId || !this.tenantId || !this.rootUrl) {
            this.loggers?.traceLogger.logTrace(0x1e19e81a /* tag_4g460 */ , TraceLevel.Error, `generateDeepLink - Missing required deep link info`);
            return null;
        }
        const deepLink = generateDeepLinkFromRoute(route, this.loggers);
        if (!deepLink) {
            this.loggers?.traceLogger.logTrace(0x1e19e819 /* tag_4g46z */ , TraceLevel.Error, `generateDeepLink - Unable to generate DeepLink from Route`);
            return null;
        }
        let contextObject = {
            subEntityId: deepLink
        };
        if (channelTab) {
            if (!this.channelId) {
                this.loggers?.traceLogger.logTrace(0x1e19e818 /* tag_4g46y */ , TraceLevel.Error, `generateDeepLink - Missing channelId for channel tab`);
                return null;
            }
            contextObject = {
                ...contextObject,
                channelId: this.channelId
            };
        }
        const teamAppLinkUrl = `${this.rootUrl}/l/entity`;
        const webUrl = `https://tasks.teams.microsoft.com/teamsui/personalApp/alltasklists`;
        const context = JSON.stringify(contextObject);
        const fullDeepLinkUrl = `${teamAppLinkUrl}/${appId}/${pageId}` + `?tenantId=${this.tenantId}` + `&webUrl=${encodeURIComponent(webUrl)}` + `&context=${encodeURIComponent(context)}`;
        return fullDeepLinkUrl;
    }
    /**
     * Ensures the ringId value is one of accepted/valid values
     * @param ringId
     */ normalizeRingId(ringId) {
        if (typeof ringId !== "string" || ringId.trim().length === 0) {
            return null;
        }
        const normalRingId = stringToEnum(ringId.trim().toLowerCase(), TeamsRingId);
        return normalRingId;
    }
    /**
     * Maps Teams theme to Uno theme
     * @param theme Teams theme, possible values are "default", "dark", "contrast"
     * @returns Uno theme
     */ mapToUnoTheme(theme) {
        switch(theme){
            case TeamsTheme.Dark:
                return PlannerTheme.Dark;
            case TeamsTheme.Contrast:
                return PlannerTheme.HighContrast;
            case TeamsTheme.Default:
            default:
                this.loggers?.traceLogger.logTrace(0x1e19e817 /* tag_4g46x */ , TraceLevel.Info, `Unsupported theme. [Theme=${theme}]]`);
                return PlannerTheme.Light;
        }
    }
    /**
     * Returns the root URL of the Teams app (typically https://teams.microsoft.com)
     */ computeRootUrl() {
        const environment = this.getConfig().sessionMetaData.environment;
        return computeTeamsRootUrl(environment, this.loggers);
    }
    /** Register handlers like onLoad and beforeUnload handlers that are required to enable caching in MOS apps. Callback invoked if there's new route from onLoad */ setupHostCaching(onLoad, onUnload) {
        if (this.getConfig().settings.disableAppCaching) {
            return;
        }
        // Return if teamsCore Capability is not supported by host e.g. when host is outlook web
        if (!metaOSAppSDK.teamsCore.isSupported()) {
            return;
        }
        metaOSAppSDK.teamsCore.registerOnLoadHandler(async (loadContext)=>{
            // Unique identifier for the particular cached load instance
            const cachedLoadId = generateGuid();
            const teamsContext = await this.getMetaOSAppContext();
            const userClickTime = teamsContext.app.userClickTime;
            const markExtraData = {
                cachedLoadId,
                userClickTime: String(userClickTime)
            };
            this.performanceMarker.mark(MarkerTag.CachedLoadStart, markExtraData);
            // With Teams App Caching, it's possible to reuse the same webview across different EntityIds (e.g., App, Tab1, Tab2, etc.)
            // As such, the TeamsContext will change, and we should reinit some state from the new TeamsContext
            // Side Note: We aren't using the teams context init state here, so don't await here as we don't want to delay invoking notifySuccess
            this.initFromMetaOSAppContext().catch(()=>{
                this.loggers?.traceLogger.logTrace(0x1e19e814 /* tag_4g46u */ , TraceLevel.Warning, `setupHostCaching - Error initializing from TeamsContext on cached load.`);
            });
            // If we can't determine the cachedRouteInfo (more specifically, the ClientFlavor), we'll fall back ClientFavor.App
            // so that the user at least has a fully usable app instead of the last cached view
            let contentUrl;
            try {
                contentUrl = new URL(loadContext.contentUrl);
            } catch (error) {
                this.loggers?.traceLogger.logTrace(0x1e19e813 /* tag_4g46t */ , TraceLevel.Warning, `setupHostCaching- Invalid Content Url`);
            }
            const cachedRouteInfo = this.getCachedRouteInfo({
                ...loadContext,
                contentUrl
            }) ?? {
                route: null,
                clientFlavor: ClientFlavor.App
            };
            await onLoad(cachedRouteInfo.clientFlavor, cachedRouteInfo.route, loadContext.contentUrl, cachedLoadId);
        });
        /**
         * This handler is called before the app is being unloaded.
         * All cleanup activities can be performed in this block.
         */ metaOSAppSDK.teamsCore.registerBeforeUnloadHandler((readyToUnload)=>{
            onUnload(readyToUnload);
            return true;
        });
    }
    async getContext() {
        const teamsContext = await this.getContextFromMetaOSAppSDK();
        const context = {
            locale: teamsContext.app.locale,
            theme: this.mapToUnoTheme(teamsContext.app.theme),
            sessionId: teamsContext.app.host.sessionId,
            userId: teamsContext.user?.id ?? "",
            displayName: teamsContext.user?.displayName ?? "",
            userUpn: teamsContext.user?.userPrincipalName ?? "",
            tenantId: teamsContext.user?.tenant?.id ?? "",
            ringId: this.normalizeRingId(teamsContext.app.host.ringId),
            platform: this.getTelemetryPlatform(teamsContext.app.host.clientType),
            isGuestUser: teamsContext.team?.userRole === metaOSAppSDK.UserTeamRole.Guest,
            isTenantAdmin: teamsContext.team?.userRole === metaOSAppSDK.UserTeamRole.Admin,
            userTeamsLicenseType: teamsContext.user?.tenant?.teamsSku,
            teamId: teamsContext.team?.internalId,
            teamName: teamsContext.team?.displayName,
            // groupId is null in case of Shared channels, hence we populate groupId with ownerGroupId in case of shared channels to use single parameter at multiple places
            groupId: teamsContext.team?.groupId ?? teamsContext.channel?.ownerGroupId,
            channelId: teamsContext.channel?.id,
            channelName: teamsContext.channel?.displayName,
            chatId: teamsContext.chat?.id,
            messageId: teamsContext.app.parentMessageId,
            replyId: teamsContext.app.messageId,
            tabId: teamsContext.channel?.id ? teamsContext.page?.id : undefined,
            channelType: this.convertTeamsChannelType(teamsContext.channel?.membershipType),
            ownerTenantId: teamsContext.channel?.ownerTenantId,
            isBackgroundLoad: teamsContext.page.isBackgroundLoad ?? false,
            frameContext: teamsContext.page?.frameContext,
            extraLoggingInfo: ()=>({
                    XSessionId: teamsContext.app.host.sessionId,
                    XLicenseType: teamsContext.user?.tenant?.teamsSku ?? ""
                })
        };
        return context;
    }
    /**
     * Initializes state from current TeamsContext (which can change when load from App Cache)
     * e.g., ChannelTab -> App, or ChannelTab1 -> ChannelTab2
     */ async initFromMetaOSAppContext() {
        const teamsContext = await this.getContextFromMetaOSAppSDK();
        // normalize ring as it can be empty, undefined or garbage sometimes
        this.ringId = this.normalizeRingId(teamsContext.app?.host?.ringId);
        this.pageId = teamsContext.page.id;
        this.isChannelTab = isChannelTab(this.pageId) || isV1UnoEntityId(this.pageId);
        this.channelId = teamsContext.channel?.id ?? null;
        this.appId = this.getHostAppId(this.isChannelTab, this.pageId);
        this.tenantId = teamsContext.user?.tenant?.id ?? null;
    }
    async setTeamsTabConfig(config, writeGuard) {
        if (writeGuard != null) {
            // setConfig writes to whichever tab is live now, so if the user switched tabs (or the route
            // changed) since this write was decided, abort to avoid corrupting the other tab's config.
            const liveContext = await this.getContext();
            const tabIdMismatch = liveContext.tabId !== writeGuard.expectedTabId;
            const isStale = writeGuard.isStale();
            if (tabIdMismatch || isStale) {
                // Tab ids are EUII, so log only the boolean reasons.
                this.loggers?.traceLogger.logTrace(0x1d80c65c /* tag_36mz2 */ , TraceLevel.Info, `setTeamsTabConfig aborted: tab identity changed since the write was decided. [tabIdMismatch=${tabIdMismatch}][isStale=${isStale}]`);
                return;
            }
        }
        await metaOSAppSDK.pages.config.setConfig({
            entityId: config.entityId,
            suggestedDisplayName: config.displayName,
            contentUrl: config.contentUrl,
            removeUrl: config.removeUrl,
            websiteUrl: config.websiteUrl
        });
    }
    async getTeamsTabConfig() {
        const config = await metaOSAppSDK.pages.getConfig();
        return {
            displayName: config.suggestedDisplayName,
            entityId: config.entityId ?? "",
            contentUrl: config.contentUrl ?? "",
            removeUrl: config.removeUrl ?? "",
            websiteUrl: config.websiteUrl ?? ""
        };
    }
    /**
     * Parses the route from the teams loadContext in the cached app scenario
     * @param loadContext The load context from the Teams SDK
     */ getCachedRouteInfo(loadContext) {
        try {
            const parsedContentUrl = this.parseContentUrl(loadContext.contentUrl.toString());
            // There are different EntityIds (think of it as different "app type"). We have Tab, Message, and App
            const tabRoute = tryParseTabRoute(loadContext.entityId, parsedContentUrl?.subEntityId, undefined, this.loggers, this.configProvider);
            if (tabRoute) {
                return {
                    route: tabRoute,
                    clientFlavor: ClientFlavor.Tab
                };
            }
            if (parsedContentUrl == null) {
                this.loggers?.traceLogger.logTrace(0x1e19e812 /* tag_4g46s */ , TraceLevel.Info, `getCachedRouteInfo. No parsedContentUrl - Just return]`);
                return null;
            }
            const messageRoute = tryParseMessageRoute(parsedContentUrl.entityId);
            if (messageRoute) {
                return {
                    route: messageRoute,
                    clientFlavor: ClientFlavor.TaskFromMessage
                };
            }
            const appRoute = tryParseAppRoute(parsedContentUrl.entityId, parsedContentUrl.subEntityId, this.loggers, this.configProvider);
            if (appRoute) {
                return {
                    route: appRoute,
                    clientFlavor: ClientFlavor.App
                };
            }
            // Note: It's possible we are in App scenario (and there's no route to parse), in which case, return null route
            //       (signals no route override, use existing/default route)
            //       Example: User opens UNO Teams App and navigates around. Then user goes to Teams Chat. Then user quickly
            //       comes back to UNO Teams App. There is no subEntityId to parse route, and we want user be where they left off.
            if (isTeamsAppEntityId(parsedContentUrl.entityId)) {
                return {
                    route: null,
                    clientFlavor: ClientFlavor.App
                };
            }
        } catch (e) {
            this.loggers?.traceLogger.logTrace(0x1e19e811 /* tag_4g46r */ , TraceLevel.Warning, `getCachedRouteInfo - Error processing route from deep link on cached load. ${generateLogStringFromError(e)}`);
            return null;
        }
        this.loggers?.traceLogger.logTrace(0x1e19e810 /* tag_4g46q */ , TraceLevel.Warning, `getCachedRouteInfo - Unable to parse route from deep link on cached load.`);
        return null;
    }
    /**
     * Parses the content URL to extract entityId and subEntityId
     * @param contentUrl The content URL to parse
     * @returns Parsed content URL with entityId and subEntityId, or null if parsing fails
     */ parseContentUrl(contentUrl) {
        if (!contentUrl) {
            return null;
        }
        try {
            const url = new URL(contentUrl);
            const params = new URLSearchParams(url.search);
            const entityId = decodeURIComponent(params.get("entityId") ?? "");
            const subEntityId = decodeURIComponent(params.get("subEntityId") ?? "");
            const planRoute = decodeURIComponent(params.get("planRoute") ?? "");
            // In case of latest Uno Tab experience (using v1 tab format), the subEntityId is passed as planRoute for tab route
            // Else it is passed as subEntityId (legacy, deeplink to task in tab).
            const parsedContentUrl = {
                entityId,
                subEntityId: subEntityId === "" ? planRoute : subEntityId
            };
            return parsedContentUrl;
        } catch (e) {
            this.loggers?.traceLogger.logTrace(0x1e19e80f /* tag_4g46p */ , TraceLevel.Warning, `parseContentUrl - Error parsing content URL`);
            return null;
        }
    }
    /**
     * Returns the Teams AppId based on the environment
     */ getHostAppId(isChannelTab, pageId) {
        if (!pageId) {
            return null;
        }
        if (isChannelTab) {
            if (!this.configProvider) {
                this.loggers?.traceLogger.logTrace(0x1e05229c /* tag_4bsk2 */ , TraceLevel.Warning, `getHostAppId - configProvider not yet available, falling back to pageIdToAppIdMap`);
                return pageIdToAppIdMap[pageId] ?? null;
            }
            const environment = this.getConfig().sessionMetaData.environment;
            switch(environment){
                case Environment.Ppe:
                    return TeamsAppId.PPE;
                case Environment.Prod:
                case Environment.Gcc:
                case Environment.Gcch:
                case Environment.Dod:
                case Environment.Agc:
                case Environment.Bleu:
                case Environment.Delos:
                    return TeamsAppId.Prod;
                default:
                    ErrorUtilities.unreachableCase(environment, (log)=>{
                        this.loggers?.traceLogger.logTrace(0x1e0568cc /* tag_4bw9m */ , TraceLevel.Error, log);
                    });
                    break;
            }
        }
        return pageIdToAppIdMap[pageId] ?? null;
    }
    /**
     * Converts Teams SDK channel  type to our expected channel type format
     * @param teamsChannelType The channel type returned from Teams SDK (e.g., "Regular", "Private", "Shared")
     * @returns The formatted teams channel type for our ITeamsContext interface ("Standard", "Private", "Shared")
     */ convertTeamsChannelType(teamsChannelType) {
        switch(teamsChannelType){
            case "Regular":
                return TeamsChannelType.Standard;
            case "Private":
                return TeamsChannelType.Private;
            case "Shared":
                return TeamsChannelType.Shared;
            default:
                this.loggers?.traceLogger.logTrace(0x1e108381 /* tag_4eiob */ , TraceLevel.Warning, `Unknown teams channel type: ${teamsChannelType}. Defaulting to Standard.`);
                return TeamsChannelType.Standard;
        }
    }
    constructor(...args){
        super(...args), this.isChannelTab = null, this.channelId = null;
    }
}
